<template>
  <div class="instructions-container app-pattern-chapter">
    <h6>{{ $t("buttonhole_calculator.Instructions") }}</h6>
    <div v-if="!props.selectedSuggestion">
      <div class="no-selection-message">
        {{ $t("buttonhole_calculator.No_selection_message") }}
      </div>
      <div class="no-selection-message">
        {{ $t("buttonhole_calculator.No_selection_message_2") }}
      </div>
    </div>
    <div v-else>
      <div class="instructions-text">
        {{
          $t("buttonhole_calculator.Before_starting", {
            initialStitchCount: props.initialStitchCount,
          })
        }}
        <span v-if="stitchCountDiff != 0">
          {{
            $t("buttonhole_calculator.Before_starting_2", {
              initialStitchCount: props.initialStitchCount,
            })
          }}
        </span>
      </div>

      <ul class="app-pattern-indented-list">
        <div>
          <li>
            <div class="instructions-text">
              <b>{{ $t("buttonhole_calculator.First_row_ws_") }}</b>
              <span v-if="props.ribPatternRepetition == 2">
                <span v-if="stitchCountDiff < 0">
                  {{
                    $t("buttonhole_calculator.First_row_description_single_rib_decreases", {
                      decreaseStitchCount: Math.abs(stitchCountDiff),
                    })
                  }}
                </span>
                <span v-else-if="stitchCountDiff > 0">
                  {{
                    $t("buttonhole_calculator.First_row_description_single_rib_increases", {
                      increaseStitchCount: Math.abs(stitchCountDiff),
                    })
                  }}
                </span>
                <span v-else>
                  {{ $t("buttonhole_calculator.Odd_row_description_single_rib") }}
                </span>
              </span>
              <span v-else-if="props.ribPatternRepetition == 4">
                <span v-if="stitchCountDiff < 0">
                  {{
                    $t("buttonhole_calculator.First_row_description_double_rib_decreases", {
                      decreaseStitchCount: Math.abs(stitchCountDiff),
                    })
                  }}
                </span>
                <span v-else-if="stitchCountDiff > 0">
                  {{
                    $t("buttonhole_calculator.First_row_description_double_rib_increases", {
                      increaseStitchCount: Math.abs(stitchCountDiff),
                    })
                  }}
                </span>
                <span v-else>
                  {{ $t("buttonhole_calculator.Odd_row_description_double_rib") }}
                </span>
              </span>
              <information-bubble
                v-if="stitchCountDiff != 0"
                class="hint-icon"
                icon="mdi-lightbulb"
                :colors-inverted="true"
                width="medium"
                position="left"
              >
                <span v-if="stitchCountDiff < 0">
                  {{ $t("buttonhole_calculator.Decrease_hint") }}
                </span>
                <span v-else>
                  {{ $t("buttonhole_calculator.Increase_hint") }}
                </span>
              </information-bubble>
            </div>
          </li>
          <li>
            <div class="instructions-text">
              <b>{{ $t("buttonhole_calculator.Second_row_rs_") }}</b>
              <span v-if="props.ribPatternRepetition == 2">
                {{ $t("buttonhole_calculator.Second_row_description_single_rib") }}
              </span>
              <span v-else-if="props.ribPatternRepetition == 4">
                {{ $t("buttonhole_calculator.Second_row_description_double_rib") }}
              </span>
            </div>
          </li>
          <li>
            <div class="instructions-text">
              <b>{{ $t("buttonhole_calculator.Third_row_ws_") }}</b>
              <span v-if="props.ribPatternRepetition == 2">
                {{ $t("buttonhole_calculator.Odd_row_description_single_rib") }}
              </span>
              <span v-else-if="props.ribPatternRepetition == 4">
                {{ $t("buttonhole_calculator.Odd_row_description_double_rib") }}
              </span>
            </div>
          </li>
        </div>
      </ul>

      <div class="instructions-text">
        {{ $t("buttonhole_calculator.Repeat_row_2_and_3_until_half_width") }}
      </div>
      <ul class="app-pattern-indented-list">
        <li>
          <div class="instructions-text">
            <b>{{ $t("buttonhole_calculator.Buttonhole_row_1_rs_") }}</b>
            <span v-if="props.ribPatternRepetition == 2">
              {{
                $t("buttonhole_calculator.Buttonhole_row_1_single_rib_description", {
                  stitchesBetweenButtonholesMinusOne: props.selectedSuggestion.intervalStitchCount - 1,
                  buttonholeCount: props.selectedSuggestion.buttonCount,
                })
              }}
            </span>
            <span v-else-if="props.ribPatternRepetition == 4">
              {{
                $t("buttonhole_calculator.Buttonhole_row_1_double_rib_description", {
                  stitchesBetweenButtonholesMinusTwo:
                    props.selectedSuggestion.intervalStitchCount - 2,
                  buttonholeCount: props.selectedSuggestion.buttonCount,
                })
              }}
            </span>
          </div>
        </li>
        <li>
          <div class="instructions-text">
            <b>{{ $t("buttonhole_calculator.Buttonhole_row_2_ws_") }}</b>
            <span v-if="props.ribPatternRepetition == 2">
              {{ $t("buttonhole_calculator.Odd_row_description_single_rib") }}
            </span>
            <span v-else-if="props.ribPatternRepetition == 4">
              {{
                $t("buttonhole_calculator.Buttonhole_row_2_double_rib_description", {
                  stitchesBetweenButtonholes: props.selectedSuggestion.intervalStitchCount,
                  buttonholeCount: props.selectedSuggestion.buttonCount,
                })
              }}
            </span>
          </div>
        </li>
      </ul>

      <div class="instructions-text">
        {{ $t("buttonhole_calculator.Continue_knitting_in_rib_pattern_and_bind_off") }}
      </div>

      <div class="instructions-text">
        {{ $t("buttonhole_calculator.Work_the_other_button_band") }}
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from "vue";
import InformationBubble from "../../components/InformationBubble.vue";

const props = defineProps({
  initialStitchCount: {
    type: Number,
    required: true,
  },
  selectedSuggestion: {
    type: Object,
    default: null,
  },
  ribPatternRepetition: {
    type: Number,
    required: true,
  },
});

const selectedId = ref("1.0");
const emit = defineEmits(["update:selected"]);

function onSelectionToggled(id) {
  selectedId.value = id;
  emit("update:selected", id);
}

const stitchCountDiff = computed(() => {
  if (props.selectedSuggestion?.stitchCount) {
    return props.selectedSuggestion.stitchCount - props.initialStitchCount;
  }
  return null;
});
</script>

<style lang="scss" scoped>
@import "../../../scss/pattern.scss";

.instructions-container {
  margin-top: 1.5rem;
  text-align: left;
}

h6 {
  color: var(--knittable-lav-darker);
  margin-bottom: 1rem;
  text-align: left;
}

p {
  color: var(--knittable-lav-dark);
  text-align: left;
}

.no-selection-message {
  color: var(--knittable-lav);
  font-style: italic;
  margin-bottom: 1rem;
}

.instructions-text {
  margin-bottom: 1rem;
  margin-top: 1rem;
}

.hint-icon {
  color: var(--knittable-lav);
  margin-left: 0.5rem;
}
</style>
